import React, {useEffect, useId, useState, useMemo} from 'react';
import FormField from '../FormField/FormField';
import SubmitButton from '../SubmitButton/SubmitButton';
import './CreateWorkplace.css';
import {getWorkplaceUsers, sendCreateWorkplace} from '../../api/workplaceApi';
import {fetchRoles} from '../../api/rolesApi';

const requiredFields = ['name', 'price'];
const textareaFieldStyle = {select: 'create-workplaces__select'};

const CreateWorkplace = ({
  handleClose,
  isEmployeeFormOpen,
  selectedItemId,
  departmentId,
  updateDepartmentData,
  selectedTab,
}) => {
  const formId = useId();

  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [formState, setFormState] = useState({
    name: '',
    status: null,
    comment: '',
    price: '',
    role_id: null,
    errors: {},
  });

  const fieldConfigurations = useMemo(
    () => [
      {key: 'name', label: 'Наименование', type: 'text'},
      {key: 'price', label: 'Цена', type: 'number'},
      {
        key: 'role_id',
        label: 'Роль',
        type: 'select',
        placeholder: 'Выберите роль',
        options: roles,
        isMulti: false, // делаем мультиселект
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employees]
  );

  useEffect(() => {
    const loadFormData = async () => {
      try {
        const [employeesData, rolesData] = await Promise.all([
          getWorkplaceUsers({organisation_id: selectedItemId}),
          fetchRoles(selectedItemId),
        ]);

        setEmployees(
          employeesData.data?.map(({id, last_name}) => ({
            value: id,
            label: last_name,
          })) ?? []
        );

        setRoles(
          rolesData.data?.map(({id, name}) => ({value: id, label: name})) ?? []
        );
      } catch (error) {
        console.error('Ошибка загрузки данных формы:', error);
      }
    };

    if (selectedItemId) loadFormData();
  }, [selectedItemId]);

  const validateField = (fieldName, value) => {
    if (!requiredFields.includes(fieldName)) return '';

    const isEmpty =
      !value ||
      (typeof value === 'string' && !value.trim()) ||
      (typeof value === 'object' && !value?.value);

    return isEmpty ? 'Поле обязательно' : '';
  };

  const handleInputChange = ({target: {name, value}}) => {
    setFormState((prev) => ({
      ...prev,
      [name]: value,
      errors: {
        ...prev.errors,
        [name]: validateField(name, value),
      },
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      const error = validateField(field, formState[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setFormState((prev) => ({...prev, errors: newErrors}));
    return isValid;
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await sendCreateWorkplace({
        organisation_id: selectedItemId,
        name: formState.name,
        department_id: departmentId,
        price: formState.price,
        role_id: formState.role_id,
      });

      updateDepartmentData(selectedTab);
      handleClose();
      resetForm();
    } catch (error) {
      console.error('Ошибка при создании рабочего места:', error);
    }
  };

  const resetForm = () => {
    setFormState({
      name: '',
      role_id: null,
      worker: null,
      status: null,
      comment: '',
      price: '',
      errors: {},
    });
  };

  const renderField = (field) => {
    return (
      <FormField
        key={`${field.key}-${formId}`}
        label={field.label}
        name={field.key}
        value={formState[field.key] || ''}
        onChange={handleInputChange}
        error={formState.errors[field.key]}
        required={requiredFields.includes(field.key)}
        options={field.options}
        isMulti={field.isMulti}
        type={field.type}
        modifierClassesFormField={textareaFieldStyle[field.type]}
      />
    );
  };

  return (
    <>
      <div
        className={`overlay ${isEmployeeFormOpen ? 'show' : 'hide'}`}
        onClick={handleClose}
      />

      <section
        className={`create-employee ${isEmployeeFormOpen ? 'show' : 'hide'}`}
      >
        <div className='create-employee__form'>
          <div className='create-employee__header'>
            <h2 className='create-employee__header-title'>
              Добавить рабочее место
            </h2>
            <SubmitButton
              className='create-employee__save-button'
              text='Сохранить'
              type='submit'
              onClick={submitForm}
            />
            <button onClick={handleClose} className='profile__close-btn' />
          </div>

          <form className='create-employee__form-container'>
            {fieldConfigurations.map(renderField)}
          </form>
        </div>
      </section>
    </>
  );
};

export default CreateWorkplace;
